
"use client";

import React from "react";
import Image from "next/image";
import Link from "next/link";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import { ExpertSection } from "@/.storyblok/types/319751/storyblok-components";
import { SbBlokData, storyblokEditable } from "@storyblok/react";

interface ExpertSectionProps {
  blok?: ExpertSection & SbBlokData;
}

const ExpertSectionComponent: React.FC<ExpertSectionProps> = ({ blok }) => {
  if (!blok) return null;

  const { title, description, team_members } = blok;

  if (!team_members?.length) {
    return null;
  }

  const isSingleExpert = team_members.length === 1;

  if (isSingleExpert) {
    const expert = team_members[0];

    return (
      <div {...storyblokEditable(blok)} className="expert-section pb-5">
        <div className="container">
          {title && <h2 className="text-primary text-center mb-4">{title}</h2>}
          {description && <p className="text-center mb-3">{description}</p>}

          <div className="d-flex flex-column align-items-center text-center">
            {expert.image?.filename && (
              <div className="mb-2">
                <Image
                  src={expert.image.filename}
                  alt={expert.name || ""}
                  width={160}
                  height={160}
                  className="js-lazy-load rounded-circle"
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                  loading="lazy"
                  quality={75}
                />
              </div>
            )}

            <div className="mb-4">
              <div className="lead fw-bold text-dark mb-2">
                {expert.name}
                {expert.linkedin_url?.cached_url && (
                  <Link
                    href={expert.linkedin_url.cached_url}
                    className="ms-2 text-primary"
                    target="_blank"
                    rel="noopener"
                  >
                    <i className="fab fa-linkedin" />
                  </Link>
                )}
              </div>
              {expert.position && (
                <div className="text-muted lead">{expert.position}</div>
              )}
              {expert.colleges && (
                <div className="text-muted small">{expert.colleges}</div>
              )}
            </div>

            {expert.quote && (
              <div className="col-lg-18 col-xl-14 mx-auto">
                <blockquote className="unstyled text-center mb-4">
                  {expert.quote}
                </blockquote>
              </div>
            )}

            {expert.blog_link?.cached_url && (
              <div className="mt-0">
                <Link
                  className="btn btn-outline-primary"
                  href={expert.blog_link.cached_url}
                >
                  Colleague spotlight
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div {...storyblokEditable(blok)} className="expert-section pb-5">
      <div className="container">
        {title && <h2 className="text-primary text-center mb-4">{title}</h2>}
        {description && <p className="text-center mb-3">{description}</p>}

        <Swiper
          modules={[Navigation, Pagination, Autoplay]}
          spaceBetween={30}
          slidesPerView={1}
          navigation={true}
          pagination={{
            clickable: true,
            renderBullet: (index, className) => {
              return `<span class="${className} custom-dot"></span>`;
            },
          }}
          autoplay={{ delay: 5000 }}
          loop
          className="expert-slider"
        >
          {team_members.map((expert) => (
            <SwiperSlide key={expert._uid}>
              <div className="d-flex flex-column align-items-center text-center">
                {expert.image?.filename && (
                  <div className="mb-2">
                    <Image
                      src={expert.image.filename}
                      alt={expert.name || ""}
                      width={160}
                      height={160}
                      className="js-lazy-load rounded-circle"
                      sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 33vw"
                      loading="lazy"
                      quality={75}
                    />
                  </div>
                )}

                <div className="mb-4">
                  <div className="lead fw-bold text-dark mb-2">
                    {expert.name}
                    {expert.linkedin_url?.cached_url && (
                      <Link
                        href={expert.linkedin_url.cached_url}
                        className="ms-2 text-primary"
                        target="_blank"
                        rel="noopener"
                      >
                        <i className="fab fa-linkedin" />
                      </Link>
                    )}
                  </div>
                  {expert.position && (
                    <div className="text-muted lead">{expert.position}</div>
                  )}
                  {expert.colleges && (
                    <div className="text-muted small">{expert.colleges}</div>
                  )}
                </div>

                {expert.quote && (
                  <div className="col-lg-18 col-xl-14 mx-auto">
                    <blockquote className="unstyled text-center mb-4">
                      {expert.quote}
                    </blockquote>
                  </div>
                )}

                {expert.blog_link?.cached_url && (
                  <div className="mt-0">
                    <Link
                      className="btn btn-outline-primary"
                      href={expert.blog_link.cached_url}
                    >
                      Colleague spotlight
                    </Link>
                  </div>
                )}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
};

export default ExpertSectionComponent;